/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-unresolved */
import React, { KeyboardEvent, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { SecurityModel } from 'api';
import {
  CAP_TABLE_CELLS,
  CAP_TABLE_FIELDS,
  CAP_TABLE_TOTAL_FIELDS,
  INVESTMENT_LOAN_ROWS,
  ROWS_TO_RESET_WHEN_CHANGING_SECURITY,
  SHARES_LEDGER_ROW_NUMBER,
} from 'common/constants/fund-ownership';
import { CustomSelect, withTooltip } from 'components';
import { useFeatureSpreadsheet } from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { useFundOwnershipContext } from 'context/FundOwnershipContext';
import { CellValue, ICell } from 'models/interfaces/spreadsheet-types/core-types';
import emptyDialogData from 'pages/CapTable/fund-ownership/data/sharesLedgerTemplate.json';
import { FundOwnershipCell } from 'pages/CapTable/fund-ownership/types';
import { reverseParser } from 'pages/CapTable/fund-ownership/utilities';
import { toString } from 'utilities';

interface SecurityListProps {
  cell: ICell;
  onCommit: (value: string | null, event?: KeyboardEvent<Element> | null | undefined) => void;
  onRevert: () => void;
}

const SecurityList = ({ cell, ...restProps }: SecurityListProps) => {
  const { onCommit, onRevert } = restProps;
  const { cells, columns, tableData, onCellsChanged } = useFeatureSpreadsheet();

  const { columnLegend, value: cellValue, key, readOnly } = cell;
  const { securityListValues, spreadsheetTableData, fieldAttributes } = useFundOwnershipContext();

  const replaceCaptableValues = ({
    capTableData,
    capTableKey,
    capTableFields,
    newSecurity,
  }: {
    capTableData: { [key: string]: FundOwnershipCell };
    capTableKey: string;
    capTableFields: string[];
    newSecurity: any;
  }) => {
    const tmpState = { ...capTableData };
    if (newSecurity && capTableFields.includes(tmpState[capTableKey].alias)) {
      tmpState[capTableKey].value = newSecurity[tmpState[capTableKey].alias];
    }
    if (CAP_TABLE_TOTAL_FIELDS.includes(tmpState[capTableKey].alias)) {
      tmpState[capTableKey].value = tableData?.capTable[tmpState[capTableKey].alias];
    }

    return tmpState;
  };

  const resetValues = (newValue: string | number | null | undefined) => {
    const tmpState = { ...cells };
    const newSecurity
      = tableData?.capTable.securities.find((security: SecurityModel) => toString(security.id) === toString(newValue))
      ?? {};

    ROWS_TO_RESET_WHEN_CHANGING_SECURITY.forEach(rowNumber => {
      const cellKey = columnLegend! + rowNumber;

      if (tmpState[cellKey]) {
        // Set values to 0
        tmpState[cellKey].value = tmpState[cellKey].defaultValue as CellValue;

        // Reset captable values
        const updatedCell = replaceCaptableValues({
          capTableData: tmpState,
          capTableKey: cellKey,
          capTableFields: CAP_TABLE_FIELDS,
          newSecurity,
        });

        tmpState[cellKey] = updatedCell[cellKey];

        if (INVESTMENT_LOAN_ROWS.includes(rowNumber)) {
          tmpState[cellKey].expr = '0';
        }
        // Reset shares ledger
        if (rowNumber === SHARES_LEDGER_ROW_NUMBER) {
          tmpState[cellKey].value = emptyDialogData;
        }
      }
    });

    CAP_TABLE_CELLS.forEach(cellAlias => {
      const cellKey = `captable_${cellAlias}_${columnLegend}`;
      if (tmpState[cellKey]) {
        tmpState[cellKey].value = newSecurity[cellAlias];
      }
    });

    if (newValue) {
      onCellsChanged([{ cell, value: newValue.toString() }]);
    } else if (!isEmpty(securityListValues[columnLegend!])) {
      onCellsChanged([{ cell, value: securityListValues[columnLegend!][0].id?.toString() }]);
    }

    reverseParser({
      cells: tmpState,
      columns,
      tableData: spreadsheetTableData,
      fieldAttributes: fieldAttributes as unknown as Map<string, any>,
    });
  };

  const handleChange = (value: string | number | null | undefined) => {
    resetValues(value);
  };

  const getOptions = useMemo(() => securityListValues[columnLegend!], [securityListValues]);

  if (isEmpty(getOptions)) {
    return <span>No securities available</span>;
  }

  if (!isEmpty(getOptions)) {
    return (
      <CustomSelect
        id={`security-list-${key}`}
        value={cellValue as string}
        options={getOptions}
        onChange={handleChange}
        onCommit={onCommit}
        onRevert={onRevert}
        onEmptyValue="SELECT SECURITY"
        disabled={readOnly}
      />
    );
  }

  return <span>No securities available</span>;
};

export default withTooltip(SecurityList);
